import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Button,
  Stack,
  Alert,
  Slider,
  Box,
  Chip,
  IconButton,
} from '@mui/joy';
import { User, UserPlus, X } from '@phosphor-icons/react';
import type {
  Opportunity,
  OpportunityBoard,
  OpportunityBoardStatus,
  OpportunityFormData,
  ParsedAddress,
} from '../../../types';
import { ContactSelectionModal } from '../../contacts/ContactSelectionModal';
import { TagPicker } from '../../tags/TagPicker';
import { AddressAutocompleteInput } from '../../common/AddressAutocompleteInput';
import { UserMultiSelect } from '../../common/UserMultiSelect';
import { useAuth } from '../../../contexts/AuthContext';
import { applyPrefill, contactAddressFields } from '../../../utils/prefill';

// Internal form data type for the backend payload.
// Nullable fields are sent explicitly so a cleared value is persisted rather than
// dropped from the PATCH body.
interface OpportunityFormSubmission {
  claim_number?: string;
  policy_number?: string;
  insurance_company?: string;
  adjuster_name?: string;
  adjuster_email?: string;
  adjuster_phone?: string;
  deductible_amount?: string | number | null;
  date_of_loss?: string | null;
  gate_code?: string;
  gated_community?: boolean | null;

  name: string;
  description?: string;
  estimated_value: number; // Backend expects 'estimated_value', not 'value'
  probability: number;
  expected_close_date: string | null;
  contact: number;
  source: string;
  next_step?: string; // Backend expects 'next_step', not 'next_action'
  board?: number; // Board field for pipeline integration
  board_status?: number; // Board status field for kanban filtering
  owner: number | null; // Backend expects 'owner' for the primary rep
  additional_rep_ids?: number[]; // Replace-set; only sent when changed
  // Address fields
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zip_code: string;
  tag_ids: number[];
}
import { useCreateOpportunity, useUpdateOpportunity } from '../../../hooks/useOpportunities';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';

interface OpportunityModalProps {
  open: boolean;
  onClose: () => void;
  contactId: number;
  opportunity?: Opportunity | null;
  boardId?: number; // Add board ID for pipeline integration
  initialStatusId?: number; // Initial status for new opportunities (e.g., when adding from a specific column)
  onSaveOpportunity?: (opportunity: Partial<Opportunity>) => Promise<void>; // Callback for both create and update
  onCreateOpportunity?: (opportunity: Partial<Opportunity>) => Promise<void>; // Deprecated - use onSaveOpportunity
}

const OPPORTUNITY_SOURCES = [
  { value: 'referral', label: 'Referral' },
  { value: 'marketing', label: 'Marketing Campaign' },
  { value: 'cold_call', label: 'Cold Call' },
  { value: 'door_knocking', label: 'Door Knocking' },
  { value: 'inbound', label: 'Inbound Lead' },
  { value: 'networking', label: 'Networking Event' },
  { value: 'existing_customer', label: 'Existing Customer' },
  { value: 'partner', label: 'Partner Referral' },
  { value: 'other', label: 'Other' },
];

// Turn a thrown API error into something readable. The client attaches the parsed
// DRF error body as `responseData`.
// Human labels for API field keys that appear in the error banner.
const FIELD_LABELS: Record<string, string> = { owner: 'Primary rep', additional_rep_ids: 'Additional reps' };

const extractErrorMessage = (error: unknown, isEditing: boolean): string => {
  const fallback = `Failed to ${isEditing ? 'update' : 'create'} opportunity. Please try again.`;
  const responseData = (error as { responseData?: unknown })?.responseData;
  if (responseData && typeof responseData === 'object') {
    const messages = Object.entries(responseData as Record<string, unknown>).map(([field, detail]) => {
      const text = Array.isArray(detail) ? detail.join(' ') : String(detail);
      return field === 'detail' || field === 'non_field_errors' ? text : `${FIELD_LABELS[field] ?? field}: ${text}`;
    });
    if (messages.length > 0) return messages.join(' ');
  }
  return fallback;
};


// Rep validation errors are shown inline on their fields as well.
const extractRepFieldErrors = (error: unknown): Record<string, string> => {
  const responseData = (error as { responseData?: Record<string, unknown> })?.responseData;
  if (!responseData || typeof responseData !== 'object') return {};
  const fieldErrors: Record<string, string> = {};
  (['owner', 'additional_rep_ids'] as const).forEach((field) => {
    const detail = responseData[field];
    if (detail) fieldErrors[field] = Array.isArray(detail) ? detail.join(' ') : String(detail);
  });
  return fieldErrors;
};

const OpportunityModal: React.FC<OpportunityModalProps> = ({
  open,
  onClose,
  contactId,
  opportunity,
  boardId,
  initialStatusId,
  onSaveOpportunity,
  onCreateOpportunity: customCreateOpportunity, // Deprecated - for backward compatibility
}) => {
  // Validate opportunity prop on every render
  React.useEffect(() => {
    if (opportunity && !opportunity.id) {
      console.warn('[OpportunityModal] Opportunity missing ID');
    }
  }, [opportunity]);

  const createOpportunity = useCreateOpportunity();
  const updateOpportunity = useUpdateOpportunity();
  
  // Fetch organization users
  const { users, loading: usersLoading } = useOrganizationUsers();
  const { user: currentUser } = useAuth();
  // Set once the user picks a primary rep, so a late-loading auth user
  // never overwrites their choice (including an explicit "Unassigned").
  const ownerTouchedRef = useRef(false);
  
  // Fetch boards using react-query directly to avoid circular dependency
  const { data: boards = [] } = useQuery({
    queryKey: ['opportunity-boards'],
    queryFn: async (): Promise<OpportunityBoard[]> => {
      const response = await api.get<OpportunityBoard[] | { results: OpportunityBoard[] }>(
        '/api/crm/opportunity-boards/',
      );
      return Array.isArray(response.data) ? response.data : (response.data.results || []);
    },
    staleTime: 5 * 60 * 1000,
  });

  // Debug logging for props
  
  const isEditing = !!opportunity;
  const [isInitialized, setIsInitialized] = useState(false);
  const prevOpportunityIdRef = useRef<number | undefined>(undefined);

  const [formData, setFormData] = useState<Partial<OpportunityFormData>>({
    name: '',
    description: '',
    estimated_value: 0, // Use backend field name
    probability: 50,
    expected_close_date: '',
    stage: 0, // Keep for backward compatibility
    contact: contactId,
    source: 'inbound',
    next_step: '', // Use backend field name
    board: boardId || undefined,
    board_status: undefined, // Will be set when board statuses load
    owner: undefined, // Will be set when opportunity data loads
    // Address fields
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
  });

  // Fetch board statuses for the selected board (like Jobs app)
  // Use formData.board directly so the query refetches when board changes
  const { data: boardStatuses = [] } = useQuery({
    queryKey: ['opportunity-board-statuses', formData.board],
    queryFn: async (): Promise<OpportunityBoardStatus[]> => {
      if (!formData.board) return [];
      const response = await api.get<OpportunityBoardStatus[]>(
        `/api/crm/opportunity-board-statuses/by-board/${formData.board}/`,
      );
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: !!formData.board,
    // No staleTime - refetch immediately when board changes
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showContactSelection, setShowContactSelection] = useState(false);
  const [contactDetails, setContactDetails] = useState<{ id: number; name: string } | null>(null);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);

  // Fetch all tags
  const { data: availableTags = [], refetch: refetchTags } = useQuery({
    queryKey: ['tags'],
    queryFn: async () => {
      const response = await api.getTags();
      console.log('[OpportunityModal] Tags fetched:', response.data);
      return response.data;
    },
  });

  // Debug log when availableTags changes
  React.useEffect(() => {
    console.log('[OpportunityModal] availableTags updated:', availableTags);
  }, [availableTags]);

  // Handle creating new tags
  const handleCreateTag = async (name: string, color: string) => {
    const response = await api.createTag(name, color);
    await refetchTags(); // Refresh the tags list
    return response.data;
  };

  // Fetch contact details
  const { data: contactInfo } = useQuery({
    queryKey: ['contact', formData.contact],
    queryFn: async () => {
      if (!formData.contact) return null;
      const response = await api.get(`/api/crm/contacts/${formData.contact}/`);
      return response.data as {
        id: number;
        first_name: string;
        last_name: string;
        email?: string;
        phone?: string;
        primary_address_detail?: {
          street_address_1?: string | null;
          street_address_2?: string | null;
          city?: string | null;
          state_province?: string | null;
          postal_code?: string | null;
        } | null;
      };
    },
    enabled: !!formData.contact,
  });

  useEffect(() => {

    if (!open) {
      // Reset initialization flag and opportunity ID ref when modal closes
      setIsInitialized(false);
      ownerTouchedRef.current = false;
      prevOpportunityIdRef.current = undefined;
      return;
    }

    // Only reinitialize if opportunity ID actually changed (prevents unnecessary re-renders)
    if (opportunity?.id === prevOpportunityIdRef.current && isInitialized) {
      return;
    }

    prevOpportunityIdRef.current = opportunity?.id;

    if (opportunity) {

      // Get the owner ID - assigned_to is an object with user details, owner is the ID
      const ownerId = opportunity.assigned_to?.id || opportunity.owner || undefined;

      setFormData({
        name: opportunity.name,
        claim_number: opportunity.claim_number ?? '',
        policy_number: opportunity.policy_number ?? '',
        insurance_company: opportunity.insurance_company ?? '',
        adjuster_name: opportunity.adjuster_name ?? '',
        adjuster_email: opportunity.adjuster_email ?? '',
        adjuster_phone: opportunity.adjuster_phone ?? '',
        deductible_amount: opportunity.deductible_amount ?? null,
        date_of_loss: opportunity.date_of_loss ?? '',
        gate_code: opportunity.gate_code ?? '',
        gated_community: opportunity.gated_community ?? null,

        description: opportunity.description || '',
        estimated_value: opportunity.estimated_value || 0, // Use backend field name
        probability: opportunity.probability,
        expected_close_date: opportunity.expected_close_date?.split('T')[0] || '',
        stage: 0, // Keep for backward compatibility
        contact: opportunity.contact,
        source: opportunity.source || 'inbound', // Default if missing
        next_step: opportunity.next_step || opportunity.next_action || '', // List payload exposes it as next_action
        board: opportunity.board || boardId || undefined,
        board_status: opportunity.board_status, // Use actual board_status from opportunity
        owner: ownerId, // Store as number in formData
        additional_rep_ids: (opportunity.additional_reps || []).map((rep) => rep.id),
        // Address fields
        address_1: opportunity.address_1 || '',
        address_2: opportunity.address_2 || '',
        city: opportunity.city || '',
        state: opportunity.state || '',
        zip_code: opportunity.zip_code || '',
      });
      // Set tags from opportunity
      setSelectedTagIds(opportunity.tags ? opportunity.tags.map(tag => tag.id) : []);
      setIsInitialized(true);
    } else {
      setFormData({
        name: '',
        description: '',
        estimated_value: 0,
        probability: 50,
        expected_close_date: '',
        stage: 0,
        contact: contactId,
        source: 'inbound',
        next_step: '',
        board: boardId || undefined,
        board_status: undefined, // Will be set when board statuses load
        // The creator is the primary rep unless they choose otherwise
        // (the API treats an explicit null as "Unassigned").
        owner: currentUser?.id,
        additional_rep_ids: [],
        // Address fields
        address_1: '',
        address_2: '',
        city: '',
        state: '',
        zip_code: '',
      });
      setSelectedTagIds([]);
      setIsInitialized(true);
    }
    setErrors({});
    setSubmitError(null);
  }, [opportunity, contactId, open]); // Removed boardId - form should only reinitialize based on opportunity change

  // New opportunities default to the creator as primary rep. The auth user
  // can arrive after the form initialized, so fill it in late if untouched.
  useEffect(() => {
    if (!open || opportunity || !currentUser?.id || ownerTouchedRef.current) return;
    setFormData((prev) => (prev.owner === undefined ? { ...prev, owner: currentUser.id } : prev));
  }, [open, opportunity, currentUser?.id]);

  // Update contact details when contact info is fetched
  useEffect(() => {
    if (contactInfo) {
      setContactDetails({
        id: contactInfo.id,
        name: `${contactInfo.first_name} ${contactInfo.last_name}`.trim() || 'Unnamed Contact'
      });
    }
  }, [contactInfo]);

  // Seed the property address from the contact's primary address. The job or
  // opportunity address can legitimately differ from where the customer
  // receives mail, so this only fills fields the user has left empty and every
  // field stays editable. Editing an existing opportunity is left alone.
  useEffect(() => {
    if (opportunity || !contactInfo) return;

    const address = contactAddressFields(contactInfo);
    if (!address) return;

    setFormData(prev =>
      applyPrefill(prev, {
        address_1: address.address_1,
        address_2: address.address_2,
        city: address.city,
        state: address.state,
        zip_code: address.zip_code,
      }),
    );
  }, [contactInfo, opportunity]);

  // Set default status to first board status when board statuses load
  useEffect(() => {
    // Only set default if we're creating a new opportunity (not editing)
    // and we have board statuses loaded and no status is currently selected
    if (!opportunity && boardStatuses.length > 0 && !formData.board_status) {
      // If initialStatusId is provided, use that; otherwise use the first status
      if (initialStatusId && boardStatuses.some(s => s.id === initialStatusId)) {
        setFormData(prev => ({ ...prev, board_status: initialStatusId }));
      } else {
        // Get the first status after sorting by order
        const sortedStatuses = [...boardStatuses].sort((a, b) => (a.order || 0) - (b.order || 0));
        const firstStatus = sortedStatuses[0];
        if (firstStatus) {
          setFormData(prev => ({ ...prev, board_status: firstStatus.id }));
        }
      }
    }
  }, [boardStatuses, opportunity, formData.board_status, initialStatusId]);

  // When board changes during editing, auto-select the first status from the new board
  useEffect(() => {
    // Only run AFTER form is initialized - prevents race condition on initial load
    // When editing and board statuses load, and current status is undefined (user changed board)
    if (isInitialized && opportunity && boardStatuses.length > 0 && formData.board_status === undefined) {
      // Get the first status after sorting by order
      const sortedStatuses = [...boardStatuses].sort((a, b) => (a.order || 0) - (b.order || 0));
      const firstStatus = sortedStatuses[0];
      if (firstStatus) {
        setFormData(prev => ({ ...prev, board_status: firstStatus.id }));
      }
    }
  }, [isInitialized, boardStatuses, opportunity, formData.board_status]);

  const handleInputChange = useCallback((field: keyof OpportunityFormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [errors]);

  const handleContactSelect = useCallback((contactId: number) => {
    setFormData(prev => ({ ...prev, contact: contactId }));
    setErrors(prev => ({ ...prev, contact: '' }));
    setShowContactSelection(false);
  }, []);

  const handleRemoveContact = useCallback(() => {
    setFormData(prev => ({ ...prev, contact: undefined }));
    setContactDetails(null);
  }, []);

  const handleAddressSelect = useCallback((parsed: ParsedAddress) => {
    setFormData(prev => ({
      ...prev,
      address_1: parsed.address_1 || prev.address_1,
      city: parsed.city || prev.city,
      state: parsed.state || prev.state,
      zip_code: parsed.zip_code || prev.zip_code,
    }));
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Opportunity name is required';
    }

    if (!formData.contact) {
      newErrors.contact = 'A contact is required';
    }

    if (formData.probability === undefined || formData.probability < 0 || formData.probability > 100) {
      newErrors.probability = 'Probability must be between 0 and 100';
    }

    // Require board selection if no boardId was provided as prop
    if (!boardId && !formData.board) {
      newErrors.board = 'Board selection is required';
    }

    // Require status selection
    if (!formData.board_status) {
      newErrors.board_status = 'Status selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData.name, formData.contact, formData.estimated_value, formData.probability, formData.board, formData.board_status, boardId]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitError(null);
    setIsSubmitting(true);
    try {
      // Use board_status directly like Jobs app (no stage conversion needed).
      // `stage` is deliberately omitted - the modal has no stage control, and sending
      // one would reopen closed won/lost deals.
      const submissionData: OpportunityFormSubmission = {
        name: formData.name!,
        description: formData.description,
        estimated_value: formData.estimated_value!, // Already using backend field name
        probability: formData.probability!,
        expected_close_date: formData.expected_close_date || null,
        contact: formData.contact || contactId, // Use selected contact or fallback to original
        source: formData.source!,
        next_step: formData.next_step, // Already using backend field name
        // Cleared values must be sent explicitly so the PATCH actually removes them
        owner: formData.owner ?? null,
        address_1: formData.address_1 || '',
        address_2: formData.address_2 || '',
        city: formData.city || '',
        state: formData.state || '',
        zip_code: formData.zip_code || '',
        tag_ids: selectedTagIds,
        claim_number: formData.claim_number || '',
        policy_number: formData.policy_number || '',
        insurance_company: formData.insurance_company || '',
        adjuster_name: formData.adjuster_name || '',
        adjuster_email: formData.adjuster_email || '',
        adjuster_phone: formData.adjuster_phone || '',
        deductible_amount: formData.deductible_amount ?? null,
        date_of_loss: formData.date_of_loss || null,
        gate_code: formData.gate_code || '',
        gated_community: formData.gated_community ?? null,

      };

      // The API replaces the whole team. Send it only when it changed (or
      // when promoting a rep to primary changed it), so saving a modal opened
      // from stale data can't drop a teammate someone else just added.
      const repIds = (formData.additional_rep_ids || []).filter((id) => id !== formData.owner);
      const initialRepIds = (opportunity?.additional_reps || []).map((rep) => rep.id);
      if (!isEditing || repIds.join(',') !== initialRepIds.join(',')) {
        submissionData.additional_rep_ids = repIds;
      }

      // Add board and board_status fields (like Jobs app)
      const finalBoardId = formData.board;
      if (finalBoardId !== undefined && finalBoardId !== null) {
        submissionData.board = finalBoardId;
        if (formData.board_status) {
          submissionData.board_status = formData.board_status;
        }
      }

      // Use the callback function if provided (preferred for both create and update)
      const callbackFn = onSaveOpportunity || customCreateOpportunity;
      
      if (callbackFn) {
        // Use the callback for both create and update when provided
        
        if (isEditing && opportunity && opportunity.id) {
          // For updates, include the ID and operation type in the data
          
          // Ensure ID is a valid number
          const opportunityId = typeof opportunity.id === 'string' ? parseInt(opportunity.id, 10) : opportunity.id;
          if (isNaN(opportunityId) || !opportunityId) {
            alert('Error: Invalid opportunity ID. Cannot update.');
            return;
          }
          
          const updateData = { 
            ...submissionData, 
            id: opportunityId,
            _isUpdate: true // Use a flag that won't conflict with types
          };
          
          await callbackFn(updateData as any);
        } else {
          // For creates, ensure no ID is passed
          
          if (isEditing) {
            alert('Error: Cannot save opportunity - missing ID. This would create a duplicate. Please close and try again.');
            return; // PREVENT THE DUPLICATE CREATION
          }
          
          const { id, ...dataWithoutId } = submissionData as any;
          await callbackFn({
            ...dataWithoutId,
            _isUpdate: false // Use a flag that won't conflict with types
          } as any);
        }
      } else {
        // Fallback to direct mutations if no callback provided
        if (isEditing && opportunity && opportunity.id) {
          const result = await updateOpportunity.mutateAsync({
            id: opportunity.id,
            data: submissionData,
          });
        } else {
          const result = await createOpportunity.mutateAsync(submissionData);
        }
      }

      onClose();
    } catch (error) {
      console.error('[OpportunityModal] Submit error:', error);
      setSubmitError(extractErrorMessage(error, isEditing));
      setErrors((prev) => ({ ...prev, ...extractRepFieldErrors(error) }));
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateForm, boardId, isEditing, opportunity, contactId, selectedTagIds, onSaveOpportunity, customCreateOpportunity, updateOpportunity, createOpportunity, onClose]);

  const formatCurrency = useCallback((value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const isPending = useMemo(() => isSubmitting || createOpportunity.isPending || updateOpportunity.isPending, [isSubmitting, createOpportunity.isPending, updateOpportunity.isPending]);

  return (
    <>
    <Modal 
      open={open} 
      onClose={onClose}
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      disableScrollLock={false}
      keepMounted={false}
      sx={{ zIndex: 9998 }} // Much higher to ensure dropdowns appear above
    >
      <ModalDialog 
        variant="outlined"
        sx={{ 
          width: '90vw', 
          maxWidth: 600, 
          maxHeight: '90vh', 
          overflow: 'auto',
          position: 'relative',
          zIndex: 1500
        }}>
        <ModalClose />
        <Typography level="h3">
          {isEditing ? 'Edit Opportunity' : 'Create New Opportunity'}
        </Typography>

        {(submitError || createOpportunity.error || updateOpportunity.error) && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {submitError || `Failed to ${isEditing ? 'update' : 'create'} opportunity. Please try again.`}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <Stack spacing={2}>
            <FormControl error={!!errors.name}>
              <FormLabel>Opportunity Name *</FormLabel>
              <Input
                key={`name-${open}`}
                value={formData.name || ''}
                onChange={(e) => {
                  handleInputChange('name', e.target.value);
                }}
                placeholder="Enter opportunity name"
                autoComplete="off"
                autoFocus
                disabled={false}
              />
              {errors.name && (
                <Typography level="body-xs" color="danger">
                  {errors.name}
                </Typography>
              )}
            </FormControl>

            <Box>
              <Typography level="body-sm" sx={{ mb: 1 }}>Associated Contact *</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {contactDetails ? (
                  <>
                    <Chip
                      variant="outlined"
                      color="primary"
                      startDecorator={<User size={14} />}
                      endDecorator={
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={handleRemoveContact}
                        >
                          <X size={14} />
                        </IconButton>
                      }
                      sx={{ flex: 1 }}
                    >
                      {contactDetails.name}
                    </Chip>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="primary"
                      startDecorator={<UserPlus size={14} />}
                      onClick={() => setShowContactSelection(true)}
                    >
                      Change
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    startDecorator={<UserPlus size={16} />}
                    onClick={() => setShowContactSelection(true)}
                    sx={{ width: '100%' }}
                  >
                    Select Contact
                  </Button>
                )}
              </Box>
              {errors.contact && (
                <Typography level="body-xs" color="danger" sx={{ mt: 0.5 }}>
                  {errors.contact}
                </Typography>
              )}
            </Box>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                key={`description-${open}`}
                value={formData.description || ''}
                onChange={(e) => {
                  handleInputChange('description', e.target.value);
                }}
                placeholder="Describe the opportunity..."
                minRows={3}
                disabled={false}
              />
            </FormControl>

            <Typography level="title-md">Claim and property access (optional)</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {([
                ['claim_number', 'Claim Number'], ['policy_number', 'Policy Number'],
                ['insurance_company', 'Insurance Company'], ['gate_code', 'Gate Code'],
                ['adjuster_name', 'Adjuster Name'], ['adjuster_email', 'Adjuster Email'], ['adjuster_phone', 'Adjuster Phone'],
              ] as const).map(([field, label]) => (
                <FormControl key={field}>
                  <FormLabel>{label}</FormLabel>
                  <Input value={formData[field] || ''} onChange={(e) => handleInputChange(field, e.target.value)} />
                </FormControl>
              ))}
              <FormControl>
                <FormLabel>Deductible</FormLabel>
                <Input type="number" startDecorator="$" value={formData.deductible_amount ?? ''}
                  slotProps={{ input: { step: '0.01' } }}
                  onChange={(e) => handleInputChange('deductible_amount', e.target.value === '' ? null : e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Date of Loss</FormLabel>
                <Input type="date" value={formData.date_of_loss || ''}
                  onChange={(e) => handleInputChange('date_of_loss', e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Gated Community</FormLabel>
                <Select value={formData.gated_community == null ? '' : String(formData.gated_community)}
                  onChange={(_, value) => handleInputChange('gated_community', value === '' || value == null ? null : value === 'true')}>
                  <Option value="">Not specified</Option><Option value="true">Yes</Option><Option value="false">No</Option>
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl error={!!errors.estimated_value}>
                <FormLabel>Estimated Value</FormLabel>
                <Input
                  key={`value-${open}`}
                  type="number"
                  value={formData.estimated_value || ''}
                  onChange={(e) => {
                    handleInputChange('estimated_value', parseFloat(e.target.value) || 0);
                  }}
                  placeholder="0"
                  startDecorator="$"
                  disabled={false}
                />
                {errors.estimated_value && (
                  <Typography level="body-xs" color="danger">
                    {errors.estimated_value}
                  </Typography>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Expected Close Date</FormLabel>
                <Input
                  key={`date-${open}`}
                  type="date"
                  value={formData.expected_close_date || ''}
                  onChange={(e) => {
                    handleInputChange('expected_close_date', e.target.value);
                  }}
                  disabled={false}
                />
              </FormControl>
            </Box>

            <Box>
              <Typography level="body-sm" sx={{ mb: 1 }}>
                Probability: {formData.probability}% 
                {formData.estimated_value && formData.probability ? (
                  <Typography component="span" level="body-xs" textColor="text.secondary" sx={{ ml: 1 }}>
                    (Weighted: {formatCurrency((formData.estimated_value * formData.probability) / 100)})
                  </Typography>
                ) : null}
              </Typography>
              <Slider
                value={formData.probability || 50}
                onChange={(_, value) => {
                  handleInputChange('probability', value as number);
                }}
                min={0}
                max={100}
                step={5}
                marks={[
                  { value: 0, label: '0%' },
                  { value: 25, label: '25%' },
                  { value: 50, label: '50%' },
                  { value: 75, label: '75%' },
                  { value: 100, label: '100%' },
                ]}
              />
              {errors.probability && (
                <Typography level="body-xs" color="danger" sx={{ mt: 1 }}>
                  {errors.probability}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <FormControl error={!!errors.board}>
                <FormLabel>Board *</FormLabel>
                <Select
                  key={`board-${open}`}
                  value={formData.board || null}
                  onChange={(_, value) => {
                    handleInputChange('board', value);
                    // Reset board_status when board changes (will be set by useEffect)
                    handleInputChange('board_status', undefined);
                  }}
                  placeholder="Select a board"
                  slotProps={{
                    listbox: {
                      sx: { zIndex: 9999 }
                    }
                  }}
                  sx={{ zIndex: 1500 }}
                >
                  {(boards || []).map((board) => (
                    <Option key={board.id} value={board.id} sx={{ zIndex: 1600 }}>
                      {board.name}
                    </Option>
                  ))}
                </Select>
                {errors.board && (
                  <Typography level="body-xs" color="danger">
                    {errors.board}
                  </Typography>
                )}
              </FormControl>

              <FormControl error={!!errors.board_status}>
                <FormLabel>Status *</FormLabel>
                <Select
                  key={`status-${open}`}
                  value={formData.board_status || null}
                  onChange={(_, value) => {
                    handleInputChange('board_status', value);
                  }}
                  placeholder={!formData.board ? 'Select a board first' : boardStatuses.length === 0 ? 'Loading statuses...' : 'Select status'}
                  disabled={!formData.board || boardStatuses.length === 0}
                  slotProps={{
                    listbox: {
                      sx: { zIndex: 9999 }
                    }
                  }}
                  sx={{ zIndex: 1500 }}
                >
                  {(boardStatuses || [])
                    .sort((a, b) => (a.order || 0) - (b.order || 0))
                    .map((status) => (
                      <Option key={status.id} value={status.id} sx={{ zIndex: 1600 }}>
                        {status.name}
                      </Option>
                    ))}
                </Select>
                {errors.board_status && (
                  <Typography level="body-xs" color="danger">
                    {errors.board_status}
                  </Typography>
                )}
              </FormControl>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              <FormControl>
                <FormLabel>Source</FormLabel>
                <Select
                  key={`source-${open}`}
                  value={formData.source}
                  onChange={(_, value) => {
                    handleInputChange('source', value);
                  }}
                  slotProps={{
                    listbox: {
                      sx: { zIndex: 9999 }
                    }
                  }}
                  sx={{ zIndex: 1500 }}
                >
                  {(OPPORTUNITY_SOURCES || []).map((source) => (
                    <Option key={source.value} value={source.value} sx={{ zIndex: 1600 }}>
                      {source.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              
              <FormControl error={!!errors.owner}>
                <FormLabel>Primary rep</FormLabel>
                <Select
                  value={formData.owner !== undefined ? formData.owner.toString() : ''}
                  onChange={(_, value) => {
                    // Only update if initialized and value is not null
                    if (isInitialized && value !== null) {
                      ownerTouchedRef.current = true;
                      const nextOwner = value === '' ? undefined : parseInt(value);
                      handleInputChange('owner', nextOwner);
                      // Promoting an additional rep makes them primary instead.
                      if (nextOwner !== undefined) {
                        handleInputChange(
                          'additional_rep_ids',
                          (formData.additional_rep_ids || []).filter((id) => id !== nextOwner),
                        );
                      }
                    }
                  }}
                  placeholder="Select a user"
                  disabled={usersLoading}
                  slotProps={{
                    listbox: {
                      sx: { zIndex: 9999 }
                    }
                  }}
                  sx={{ zIndex: 1500 }}
                >
                  <Option value="">Unassigned</Option>
                  {(users || []).map((user) => (
                    <Option key={user.id} value={user.id.toString()} sx={{ zIndex: 1600 }}>
                      {user.first_name} {user.last_name}
                    </Option>
                  ))}
                </Select>
                {errors.owner && (
                  <Typography level="body-xs" color="danger">
                    {errors.owner}
                  </Typography>
                )}
              </FormControl>

              <UserMultiSelect
                label="Additional reps"
                placeholder="Add reps working this deal…"
                helperText="Additional reps can see this opportunity in their pipeline."
                value={formData.additional_rep_ids || []}
                onChange={(ids) => handleInputChange('additional_rep_ids', ids)}
                users={users || []}
                selectedFallback={opportunity?.additional_reps || []}
                excludeIds={formData.owner !== undefined ? [formData.owner] : []}
                loading={usersLoading}
                maxSelected={10}
                error={errors.additional_rep_ids || undefined}
              />
            </Box>

            {/* Address Section */}
            <Box>
              <Typography level="title-sm" sx={{ mb: 2 }}>
                Property Address
              </Typography>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Street Address</FormLabel>
                  <AddressAutocompleteInput
                    value={formData.address_1 || ''}
                    onChange={(val) => handleInputChange('address_1', val)}
                    onAddressSelect={handleAddressSelect}
                    placeholder="123 Main Street"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Address Line 2</FormLabel>
                  <Input
                    key={`address2-${open}`}
                    value={formData.address_2 || ''}
                    onChange={(e) => {
                      handleInputChange('address_2', e.target.value);
                    }}
                    placeholder="Apt, Suite, Unit, etc. (optional)"
                  />
                </FormControl>

                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 2 }}>
                  <FormControl>
                    <FormLabel>City</FormLabel>
                    <Input
                      key={`city-${open}`}
                      value={formData.city || ''}
                      onChange={(e) => {
                        handleInputChange('city', e.target.value);
                      }}
                      placeholder="City"
                    />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>State</FormLabel>
                    <Input
                      key={`state-${open}`}
                      value={formData.state || ''}
                      onChange={(e) => {
                        handleInputChange('state', e.target.value);
                      }}
                      placeholder="State"
                    />
                  </FormControl>
                  
                  <FormControl>
                    <FormLabel>Zip Code</FormLabel>
                    <Input
                      key={`zip-${open}`}
                      value={formData.zip_code || ''}
                      onChange={(e) => {
                        handleInputChange('zip_code', e.target.value);
                      }}
                      placeholder="Zip"
                    />
                  </FormControl>
                </Box>
              </Stack>
            </Box>

            <FormControl>
              <FormLabel>Next Step</FormLabel>
              <Input
                key={`next-step-${open}`}
                value={formData.next_step || ''}
                onChange={(e) => {
                  handleInputChange('next_step', e.target.value);
                }}
                placeholder="What's the next step?"
                disabled={false}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Tags</FormLabel>
              <TagPicker
                value={selectedTagIds}
                onChange={setSelectedTagIds}
                availableTags={availableTags}
                onCreateTag={handleCreateTag}
                label="" // No label - parent FormControl provides it
              />
            </FormControl>

            <Stack direction="row" spacing={2} sx={{ justifyContent: 'flex-end', mt: 3 }}>
              <Button variant="plain" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" loading={isPending}>
                {isEditing ? 'Update' : 'Create'} Opportunity
              </Button>
            </Stack>
          </Stack>
        </Box>
      </ModalDialog>
    </Modal>

    {showContactSelection && (
      <ContactSelectionModal
        open={showContactSelection}
        onClose={() => setShowContactSelection(false)}
        onContactSelect={handleContactSelect}
        existingContactIds={formData.contact ? [formData.contact] : []}
      />
    )}
  </>
  );
};

export default OpportunityModal;
